// DaySelector.tsx — horizontaler Tages-Strip für die Mobile-Tagesansicht
// (`/termine`, SP6). Zeigt ein gleitendes Fenster [value-daysBack … value+
// daysForward] (zentriert um currentDate, bedienfreundlich beim Vor-/Zurück-
// Wischen). Pro Tag eine Pill: Wochentagsbuchstabe (M D M D F S S) oben,
// Datumszahl unten. Aktiver Tag = REFINED-`aub`-Pill. Tap → onChange
// (= setCurrentDate). Optionaler Event-Punkt analog MiniMonth. scroll-snap
// horizontal; die aktive Pill zentriert sich beim Mount/value-Wechsel.

import React, { useMemo, useRef } from "react";
import moment from "moment";
import "../../utils/momentDe"; // zentrale Locale-Quelle (de, Montag-Wochenstart, SP1)

export interface DaySelectorProps {
    /** = currentDate (hervorgehoben). */
    value: Date;
    /** = setCurrentDate. */
    onChange: (d: Date) => void;
    /** Optional: Tage mit Termin bekommen einen Punkt (analog MiniMonth). */
    events?: { start: Date }[];
    /** Tage vor `value` (Default 3 → zentriertes 7-Tage-Fenster). */
    daysBack?: number;
    /** Tage nach `value` (Default 3). */
    daysForward?: number;
}

/** Erster Buchstabe des deutschen Wochentags je isoWeekday (Mo…So). */
const WD_INITIAL = ["M", "D", "M", "D", "F", "S", "S"];

const dayKey = (d: Date): string => moment(d).format("YYYY-MM-DD");

export const DaySelector: React.FC<DaySelectorProps> = ({
    value,
    onChange,
    events,
    daysBack = 3,
    daysForward = 3,
}) => {
    const stripRef = useRef<HTMLDivElement | null>(null);

    const days = useMemo(() => {
        const start = moment(value).startOf("day").subtract(daysBack, "days");
        const out: moment.Moment[] = [];
        for (let i = 0; i < daysBack + daysForward + 1; i++) {
            out.push(start.clone().add(i, "days"));
        }
        return out;
    }, [value, daysBack, daysForward]);

    const eventDayKeys = useMemo(() => {
        const set = new Set<string>();
        (events || []).forEach((e) => {
            if (e?.start) set.add(dayKey(e.start));
        });
        return set;
    }, [events]);

    const valueKey = dayKey(value);
    const todayKey = moment().format("YYYY-MM-DD");

    // Aktive Pill beim Mount/value-Wechsel horizontal zentrieren.
    const activeRef = useRef<HTMLButtonElement | null>(null);
    React.useEffect(() => {
        activeRef.current?.scrollIntoView({ inline: "center", block: "nearest" });
    }, [valueKey]);

    return (
        <div ref={stripRef} className="cal-day-strip" role="tablist" aria-label="Tag wählen">
            {days.map((d) => {
                const key = d.format("YYYY-MM-DD");
                const isActive = key === valueKey;
                const isToday = key === todayKey;
                const hasEvent = eventDayKeys.has(key);
                const wd = WD_INITIAL[d.isoWeekday() - 1];
                return (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        ref={isActive ? activeRef : undefined}
                        className={`cal-day-pill${isActive ? " is-active" : ""}${
                            isToday && !isActive ? " is-today" : ""
                        }`}
                        onClick={() => onChange(d.toDate())}
                        title={d.format("dddd, D. MMMM YYYY")}
                    >
                        <span className="cal-day-wd">{wd}</span>
                        <span className="cal-day-num">{d.date()}</span>
                        <span
                            className="cal-day-dot"
                            style={{ visibility: hasEvent ? "visible" : "hidden" }}
                            aria-hidden
                        />
                    </button>
                );
            })}
        </div>
    );
};

export default DaySelector;
